import React from 'react';
import styled from 'styled-components';
import { StyledSocialCard, StyledSocialCardWrapper } from '../styled';
import Row from 'components/Row';
import { AutoColumn } from 'components/Column';
import { TYPE } from 'theme';
import Button from 'components/Button';
import { Avatar } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { StyledIconWrapper } from 'components/Icons/Styled';
import Tooltip from 'components/TooltipInfo';
import { ReactComponent as QuestionSVG } from 'assets/images/dashboard/question.svg';
import { Social, Profile } from './config';
import SocialMediaIcon from 'components/Icons/SocialMedias';
import { SocialType } from 'constant/socialAuth';
import { SocialProfileMission } from 'gql-ops/graph/__generated__/request';
import { isMobile } from 'react-device-detect';

const Content = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: 16px;
`;

const BorderedButton = styled(Button).attrs({
    size: 'unset',
    type: 'outlineGradient',
})`
    padding: 8px 20px !important;
    border-radius: 28px !important;
    transition: all 0.3s ease;
    white-space: nowrap;
    width: 100%;
    @media (min-width: 768px) {
        width: auto;
    }

    &:hover {
        transform: translateY(-2px);
    }
`;

const RightSection = styled(Row)`
    gap: 12px;
    justify-content: flex-end;
    align-items: center;
    flex-shrink: 0;
`;

const MainText = styled(TYPE.main)`
    flex: 1;
    margin-right: 12px;
    white-space: nowrap;
`;

const Tip = styled.div`
    width: 100%;
    background-color: ${({ theme }) => `${theme.c.success}20`};
    color: ${({ theme }) => theme.c.success};
    padding: 10px;
    border-radius: 8px;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.3s ease;
`;

const StyledQuestionIcon = styled(QuestionSVG)`
    path {
        fill: #585858;
    }
    width: 16px;
    height: 16px;
    cursor: help;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const StyledAvatar = styled(Avatar)`
    width: 30px !important;
    height: 30px !important;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }
`;

const RemoveButton = styled(Button).attrs({
    type: 'outlineWhite',
    size: 'unset',
})`
    border-color: #585858;
    font-weight: 400;
    padding: 8px 20px;
    border-radius: 20px;
    transition: all 0.3s ease;
    width: 100%;

    @media (min-width: 768px) {
        width: auto;
    }

    &:not(:disabled):hover {
        background: ${({ theme }) => `${theme.c.primary02}20`};
        border-color: ${({ theme }) => theme.c.primary02};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export interface CardProps {
    social: Social;
    profile?: Profile;
    mission?: SocialProfileMission;
    connect: (type: SocialType) => void;
    disconnect: (type: SocialType) => void;
    loading?: boolean;
}

const Unconnected = ({
    social,
    connect,
    loading,
}: {
    social: Social;
    mission?: SocialProfileMission;
    connect: (type: SocialType) => void;
    loading?: boolean;
}) => {
    return (
        <Row style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Row style={{ width: 'auto' }}>
                <MainText fontSize={14}>Connect to {social.label}</MainText>
            </Row>
            {!isMobile && (
                <RightSection style={{ width: 'auto' }}>
                    <BorderedButton onClick={() => connect(social.type)} disabled={loading}>
                        {loading ? 'Connecting...' : 'Connect'}
                    </BorderedButton>
                </RightSection>
            )}
        </Row>
    );
};

const Connected = ({
    social,
    profile,
    // mission,
    disconnect,
}: {
    social: Social;
    profile: Profile;
    mission?: SocialProfileMission;
    disconnect: (type: SocialType) => void;
}) => {
    return (
        <Row style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Row gap="4px" align="center" style={{ width: 'auto' }}>
                <StyledAvatar alt={profile?.name} src={profile?.avatar} />
                <TYPE.main fontSize={14}>@{profile?.name}</TYPE.main>
                <StyledIconWrapper type="success" size={12}>
                    <DoneIcon sx={{ color: '#fff', width: '8px' }} />
                </StyledIconWrapper>
            </Row>
            {/* {!isMobile && (
                <Row gap="12px" align="center" style={{ width: 'auto' }}>
                    <RemoveButton onClick={() => disconnect(social.type)}>Remove</RemoveButton>
                    <Tooltip
                        title={
                            // mission?.unlinkable
                            //     ? 'Removing the binding will prevent you from completing social tasks in the future.'
                            'Removing social account is temporarily unavailable while social missions are in progress.'
                        }
                    >
                        <StyledQuestionIcon />
                    </Tooltip>
                </Row>
            )} */}
        </Row>
    );
};

const SocialCard = ({ social, profile, mission, connect, disconnect, loading }: CardProps) => {
    return (
        <StyledSocialCardWrapper disabled={social.disabled}>
            <StyledSocialCard disabled={social.disabled}>
                {isMobile ? (
                    <AutoColumn gap="12px" style={{ width: '100%' }}>
                        <Row gap="40px" align="center" style={{ width: '100%' }}>
                            <SocialMediaIcon type={social.type} />
                            <Content>
                                {profile?.name ? (
                                    <Connected
                                        social={social}
                                        profile={profile!}
                                        mission={mission}
                                        disconnect={disconnect}
                                    />
                                ) : (
                                    <Unconnected
                                        social={social}
                                        mission={mission}
                                        connect={connect}
                                        loading={loading}
                                    />
                                )}
                            </Content>
                        </Row>
                        {/* <Row gap="12px" align="center" style={{ width: '100%', justifyContent: 'center' }}>
                            <RemoveButton onClick={() => disconnect(social.type)} disabled={!mission?.unlinkable}>
                                Remove
                            </RemoveButton>
                            <Tooltip
                                title={
                                    mission?.unlinkable
                                        ? 'Removing the binding will prevent you from completing social tasks in the future.'
                                        : 'Removing social account is temporarily unavailable while social missions are in progress.'
                                }
                            >
                                <StyledQuestionIcon />
                            </Tooltip>
                        </Row> */}
                        {profile?.name ? null : (
                            <Row gap="12px" align="center" style={{ width: '100%', justifyContent: 'center' }}>
                                <BorderedButton onClick={() => connect(social.type)} disabled={loading}>
                                    {loading ? 'Connecting...' : 'Connect'}
                                </BorderedButton>
                            </Row>
                        )}
                    </AutoColumn>
                ) : (
                    <Row gap="40px" align="center" style={{ width: '100%' }}>
                        <SocialMediaIcon type={social.type} />
                        <Content>
                            {profile?.name ? (
                                <Connected
                                    social={social}
                                    profile={profile!}
                                    mission={mission}
                                    disconnect={disconnect}
                                />
                            ) : (
                                <Unconnected social={social} mission={mission} connect={connect} loading={loading} />
                            )}
                        </Content>
                    </Row>
                )}
            </StyledSocialCard>
        </StyledSocialCardWrapper>
    );
};

export default SocialCard;
